import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import { formatTimeAgo } from '~/utils/formatTimestamp';
import { getProjectStatusIcon } from '~/utils/projectStatus';

import { TaskProgressBar } from '~/pages/Projects/TaskProgressBar';

import type { ProjectWithTasks } from '~/types';
import { TaskStatus } from '~/types/api/enums';

export interface DashboardProjectCardProps {
  /** Project to display. Props-only — this component never fetches data. */
  project: ProjectWithTasks;
  /** Name of the product this project belongs to — self-labels the card now that no grouping container header shows it. */
  productName: string;
}

/**
 * `useProductDashboard()`'s batch endpoint (unlike `/projects`) returns each
 * project's full `tasks` array but never populates the pre-aggregated
 * `totalTasks`/`completedTasks` counts `TaskProgressBar` reads — confirmed
 * via the live API response, not assumed. Prefer the pre-aggregated counts
 * when a caller does provide them (e.g. a future dashboard payload change,
 * or this component being reused against `/projects`-shaped data), and fall
 * back to counting the raw `tasks` array otherwise, so the progress bar
 * reflects real data on both the current dashboard payload and any payload
 * shaped like the type already declares.
 */
function resolveTaskCounts(project: ProjectWithTasks): {
  completedTasks: number;
  totalTasks: number;
} {
  if (project.totalTasks !== undefined || project.completedTasks !== undefined) {
    return { completedTasks: project.completedTasks ?? 0, totalTasks: project.totalTasks ?? 0 };
  }

  const tasks = project.tasks ?? [];
  return {
    completedTasks: tasks.filter(task => task.status === TaskStatus.DONE).length,
    totalTasks: tasks.length,
  };
}

/**
 * Display card for one project on /dashboard — shared by both the In
 * Progress and Recently Completed sections, so the status icon is read from
 * the real `project.status` via the shared `getProjectStatusIcon` rather
 * than assumed: a card reaching this component is no longer IN_PROGRESS by
 * construction now that Recently Completed (always DONE) also renders
 * through it.
 *
 * A real `<Link>` (not a button + onClick navigate) so the card supports
 * native browser features — middle-click, cmd-click, "open in new tab".
 *
 * Pattern: docs/design-patterns/frontend/component-patterns.md (display-components-props-only)
 * Pattern: docs/design-patterns/frontend/accessibility.md (semantic-html-first)
 * Pattern: docs/design-patterns/frontend/design-system-consistency.md (HeroUI-consistent elevation/hover treatment)
 */
export function DashboardProjectCard({
  productName,
  project,
}: DashboardProjectCardProps): JSX.Element {
  const { completedTasks, totalTasks } = resolveTaskCounts(project);

  return (
    <Link
      to={`/projects/${project.id}`}
      className="flex h-full flex-col gap-3 rounded-lg border border-default bg-surface-elevated px-5 py-4 text-left shadow-sm transition-shadow hover:border-primary hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/50"
    >
      <div className="flex w-full items-center gap-3">
        <p className="min-w-0 flex-1 truncate text-xs font-medium text-primary-800">
          {productName}
        </p>
        <span className="shrink-0 text-xs text-foreground-600">
          {formatTimeAgo(project.updatedAt)}
        </span>
      </div>
      <div className="flex w-full items-center gap-3">
        {getProjectStatusIcon(project.status)}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-foreground">{project.title}</p>
        </div>
      </div>
      <TaskProgressBar completedTasks={completedTasks} totalTasks={totalTasks} />
    </Link>
  );
}
